// src/utils/profanityFilter.js

// List of words to filter (keeping it relatively mild but effective)
// This list can be expanded as needed
const PROFANE_WORDS = [
  // Strong profanity (including common variations)
  'fuck', 'fucking', 'fucked', 'fucker', 'fuckers', 'fucks', 'fuckin', "fuckin'",
  'shit', 'shits', 'shitting', 'shitty', 'bullshit', 'shitstorm',
  'ass', 'asshole', 'assholes', 'asses', 'badass',
  'bitch', 'bitches', 'bitching', 'bitchy',
  'damn', 'damned', 'dammit', 'goddamn', 'goddamned', 'goddamnit',
  'crap', 'crappy',
  'hell',
  'bastard', 'bastards',
  'cunt', 'cunts',
  'dick', 'dicks', 'dickhead', 'dickheads',
  'piss', 'pissed', 'pissing', 'pisses',
  'whore', 'whores',
  
  // Slurs and hate speech (important to filter)
  'nigger', 'nigga', 'niggas',
  'faggot', 'faggots', 'fag', 'fags',
  'retard', 'retarded', 'retards',
  'spic', 'spics',
  'kike', 'kikes',
  'chink', 'chinks',
  'wetback', 'wetbacks',
  'dyke', 'dykes',
  'tranny', 'trannies',
];

// Create a regex pattern that matches words (case insensitive)
const createPattern = () => {
  const escapedWords = PROFANE_WORDS.map(word => 
    word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  // Match words with optional apostrophe variations
  return new RegExp(`\\b(${escapedWords.join('|')})(in'|ing|'s)?\\b`, 'gi');
};

const PROFANITY_PATTERN = createPattern();

/**
 * Check if text contains profanity
 * @param {string} text - The text to check
 * @returns {boolean} - True if profanity is found
 */
export const containsProfanity = (text) => {
  if (!text || typeof text !== 'string') return false;
  PROFANITY_PATTERN.lastIndex = 0; // Reset regex state
  return PROFANITY_PATTERN.test(text);
};

/**
 * Get all profane words found in text
 * @param {string} text - The text to check
 * @returns {string[]} - Array of found profane words
 */
export const findProfanity = (text) => {
  if (!text || typeof text !== 'string') return [];
  PROFANITY_PATTERN.lastIndex = 0;
  const matches = text.match(PROFANITY_PATTERN);
  return matches ? [...new Set(matches.map(m => m.toLowerCase()))] : [];
};

/**
 * Censor profane words in text by wrapping them in a special marker
 * @param {string} text - The text to censor
 * @returns {{ text: string, hasProfanity: boolean, censoredWords: string[] }}
 */
export const censorText = (text) => {
  if (!text || typeof text !== 'string') {
    return { text: '', hasProfanity: false, censoredWords: [] };
  }
  
  const censoredWords = findProfanity(text);
  
  if (censoredWords.length === 0) {
    return { text, hasProfanity: false, censoredWords: [] };
  }
  
  // Replace profane words with a placeholder that includes the original word
  PROFANITY_PATTERN.lastIndex = 0;
  const censoredText = text.replace(PROFANITY_PATTERN, (match) => {
    return `[[CENSORED:${match}]]`;
  });
  
  return {
    text: censoredText,
    hasProfanity: true,
    censoredWords
  };
};

/**
 * Parse censored text and return segments for rendering
 * @param {string} censoredText - Text with [[CENSORED:word]] markers
 * @returns {Array<{ type: 'text' | 'censored', content: string }>}
 */
export const parseCensoredText = (censoredText) => {
  if (!censoredText) return [];
  
  const segments = [];
  const pattern = /\[\[CENSORED:(.*?)\]\]/g;
  let lastIndex = 0;
  let match;
  
  while ((match = pattern.exec(censoredText)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: censoredText.slice(lastIndex, match.index)
      });
    }
    
    // Add the censored word
    segments.push({
      type: 'censored',
      content: match[1]
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < censoredText.length) {
    segments.push({
      type: 'text',
      content: censoredText.slice(lastIndex)
    });
  }
  
  return segments;
};

